import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import {
  getViewParameterValue,
  ResizeNotifier,
} from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';

/**
 * Install together with the updated variant picker Liquid snippet.
 *
 * Size stays selectable.
 * Nonexistent dependent combinations are removed.
 * Existing sold-out variants stay selectable.
 * Only the latest response updates product content and the URL.
 */
export default class VariantPicker extends Component {
  #abortController;
  #requestSequence = 0;
  #pendingRequestUrl;
  #groups = [];
  #variants = null;
  #lastChanged;
  #committing = false;
  #committedSelection = [];

  #onChange = (event) => this.variantChanged(event);

  #resizeObserver = new ResizeNotifier(() =>
    this.updateVariantPickerCss()
  );

  connectedCallback() {
    super.connectedCallback();

    this.removeEventListener('change', this.#onChange);
    this.addEventListener('change', this.#onChange);

    this.#resizeObserver.observe(this);
    this.#readPicker();

    const before = this.selectedOptionsValues.join(',');

    this.#reconcile(true);
    this.updateVariantPickerCss();

    if (
      before !== this.selectedOptionsValues.join(',') &&
      this.selectedOption
    ) {
      this.fetchUpdatedSection(
        this.buildRequestUrl(this.selectedOption)
      );
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('change', this.#onChange);
    this.#resizeObserver.disconnect();

    if (!this.#committing) {
      this.#abortController?.abort();
      this.#requestSequence++;
    }
  }

  #readPicker() {
    this.#groups = this.#readGroups(this);
    this.#committedSelection = this.selectedOptionsValues;
    this.#variants = null;

    const data = this.#json(
      this.querySelector('script[data-variant-map]')
    );

    if (
      data &&
      data.count > 0 &&
      data.ids?.length === data.count &&
      data.options?.length === data.count &&
      data.available?.length === data.count
    ) {
      this.#variants = data.ids.map((id, index) => ({
        id: String(id),
        options: data.options[index],
        available: data.available[index],
      }));
    }

    // Connected products need Shopify's server-rendered response.
    if (
      this.#groups.some((group) =>
        group.values.some(
          (value) => value.dataset.connectedProductUrl
        )
      )
    ) {
      this.#variants = null;
    }
  }

  #json(script) {
    try {
      return JSON.parse(script?.textContent || 'null');
    } catch {
      return null;
    }
  }

  #readGroups(root) {
    return Array.from(
      root.querySelectorAll(
        'select.variant-option__select[data-option-position], ' +
          'fieldset[data-option-position], ' +
          'input[data-variant-single]'
      )
    )
      .filter(
        (element) => element.closest('variant-picker') === root
      )
      .map((element) => ({
        element,
        position: Number(element.dataset.optionPosition),
        name: element.dataset.optionName || '',
        values:
          element instanceof HTMLSelectElement
            ? Array.from(element.options)
            : element instanceof HTMLFieldSetElement
              ? Array.from(
                  element.querySelectorAll(
                    'input[type="radio"][data-option-value-id]'
                  )
                )
              : [element],
      }))
      .sort((a, b) => a.position - b.position);
  }

  #selected(group) {
    if (group.element instanceof HTMLSelectElement) {
      return group.element.selectedOptions[0];
    }

    if (group.element instanceof HTMLFieldSetElement) {
      return group.values.find((value) => value.checked);
    }

    return group.values[0];
  }

  #primary(groups = this.#groups) {
    // Also supports products with Flavor first and Size second.
    return (
      groups.find((group) => /\bsize\b/i.test(group.name)) ||
      groups[0]
    );
  }

  #isFlavor(group) {
    return /flavou?r/i.test(group.name);
  }

  #setSelected(group, value) {
    if (!value) return;

    if (group.element instanceof HTMLSelectElement) {
      if (value.parentElement !== group.element) {
        group.element.append(value);
      }

      group.values.forEach((option) => {
        option.selected = option === value;
        option.toggleAttribute('selected', option === value);
      });

      group.element.value = value.value;
    } else if (
      group.element instanceof HTMLFieldSetElement
    ) {
      const previous = this.#selected(group);

      group.values.forEach((input) => {
        input.checked = input === value;

        input.dataset.previousChecked = String(
          input === previous && previous !== value
        );

        input.dataset.currentChecked = String(
          input === value
        );
      });
    }

    const label = group.element
      .closest('.variant-option')
      ?.querySelector('.variant-option__swatch-value');

    if (label) {
      label.textContent = value.value;
    }
  }

  #sort(group, values) {
    if (!this.#isFlavor(group)) return values;

    return [...values].sort(
      (a, b) =>
        Number(a.dataset.optionAvailable === 'false') -
          Number(b.dataset.optionAvailable === 'false') ||
        a.value.localeCompare(b.value, undefined, {
          sensitivity: 'base',
          numeric: true,
        })
    );
  }

  #display(group, visible) {
    const selected = this.#selected(group);
    const allowed = new Set(visible);
    const ordered = this.#sort(group, visible);

    for (const value of group.values) {
      const show = allowed.has(value);

      value.hidden = !show;
      value.disabled = !show;
      value.removeAttribute('aria-disabled');

      if (value instanceof HTMLOptionElement) {
        /*
         * Remove invalid native select options.
         * Keep their nodes in group.values so another Size
         * can restore them immediately.
         */
        if (!show) {
          value.remove();
        }
      } else if (value.type === 'radio') {
        const label = value.closest('label');

        if (label) {
          label.hidden = !show;
          label.style.display = show ? '' : 'none';

          if (this.#isFlavor(group)) {
            label.style.order = String(
              ordered.indexOf(value)
            );
          }
        }
      }
    }

    if (group.element instanceof HTMLSelectElement) {
      for (const value of ordered) {
        group.element.append(value);
      }

      group.element.disabled = ordered.length === 0;
    }

    if (selected && allowed.has(selected)) {
      this.#setSelected(group, selected);
    }
  }

  #reconcile(allowServerContext = false) {
    const primary = this.#primary();

    if (!primary) return;

    /*
     * Only use local filtering with a complete variant map.
     * Never treat a truncated array as the full product.
     */
    if (!this.#variants) {
      if (allowServerContext) {
        this.#reconcileContext();
      }

      return;
    }

    let candidates = this.#variants;

    const ordered = [
      primary,
      ...this.#groups.filter((group) => group !== primary),
    ];

    for (const group of ordered) {
      const index = group.position - 1;

      const valid = group.values.filter((value) =>
        candidates.some(
          (variant) => variant.options[index] === value.value
        )
      );

      for (const value of group.values) {
        const matches = candidates.filter(
          (variant) => variant.options[index] === value.value
        );

        value.dataset.optionAvailable = String(
          matches.some((variant) => variant.available)
        );
      }

      let selected = this.#selected(group);

      const visible =
        group === primary ? group.values : valid;

      // Preserve Size. Correct dependent options only.
      if (
        group !== primary &&
        !valid.includes(selected)
      ) {
        selected = this.#sort(group, valid)[0];
      }

      if (selected) {
        this.#setSelected(group, selected);
      }

      this.#display(group, visible);

      candidates = selected
        ? candidates.filter(
            (variant) =>
              variant.options[index] === selected.value
          )
        : [];
    }

    /*
     * Refresh contextual variant IDs after all selections
     * have been reconciled.
     */
    const selected = this.#groups.map(
      (group) => this.#selected(group)?.value
    );

    for (const group of this.#groups) {
      for (const value of group.values) {
        const match = this.#variants.find((variant) =>
          this.#groups.every(
            (other, index) =>
              variant.options[other.position - 1] ===
              (
                other === group
                  ? value.value
                  : selected[index]
              )
          )
        );

        value.dataset.variantId = match?.id || '';
      }
    }
  }

  #reconcileContext() {
    const primary = this.#primary();

    for (const group of this.#groups) {
      const visible =
        group === primary
          ? group.values
          : group.values.filter(
              (value) =>
                value.dataset.variantId ||
                value.dataset.connectedProductUrl
            );

      const selected = this.#selected(group);

      if (
        group !== primary &&
        !visible.includes(selected) &&
        visible.length
      ) {
        this.#setSelected(
          group,
          this.#sort(group, visible)[0]
        );
      }

      this.#display(group, visible);
    }
  }

  updateAvailableOptions() {
    this.#reconcile(true);
  }

  updateSelectedOption(target) {
    if (typeof target === 'string') {
      target = this.#groups
        .flatMap((group) => group.values)
        .find(
          (value) =>
            value.dataset.optionValueId === target
        );
    }

    const group = this.#groups.find(
      (item) =>
        item.element === target ||
        item.values.includes(target)
    );

    if (!group) return;

    const value =
      target instanceof HTMLSelectElement
        ? target.selectedOptions[0]
        : target;

    this.#setSelected(group, value);
    this.#lastChanged = value;
    this.updateVariantPickerCss();
  }

  variantChanged(event) {
    const target = event.target;

    if (
      !(target instanceof HTMLElement) ||
      target.closest('variant-picker') !== this
    ) {
      return;
    }

    const group = this.#groups.find(
      (item) =>
        item.element === target ||
        item.values.includes(target)
    );

    // Ignore bundle dummy dropdowns.
    if (!group) return;

    this.updateSelectedOption(target);

    const selected = this.#selected(group);

    if (!selected) return;

    const linked = selected.dataset.connectedProductUrl;

    const loadsNewProduct =
      this.#isProductPage() &&
      linked &&
      new URL(linked, window.location.href).pathname !==
        new URL(
          this.dataset.productUrl,
          window.location.href
        ).pathname;

    if (!linked) {
      this.#reconcile();
    }

    const selector = loadsNewProduct
      ? 'main'
      : this.closest('featured-product-information')
        ? 'featured-product-information'
        : undefined;

    this.fetchUpdatedSection(
      this.buildRequestUrl(selected),
      selector,
      selected.dataset.optionValueId || ''
    );
  }

  #isProductPage() {
    return (
      this.dataset.templateProductMatch === 'true' &&
      !this.closest(
        'product-card, quick-add-dialog, ' +
          'quick-add-component, featured-product-information'
      )
    );
  }

  buildRequestUrl(
    selectedOption,
    source = null,
    sourceSelectedOptionsValues = []
  ) {
    const base =
      selectedOption?.dataset.connectedProductUrl ||
      this.#pendingRequestUrl ||
      this.dataset.productUrl;

    const url = new URL(base, window.location.href);

    url.searchParams.delete('variant');
    url.searchParams.delete('option_values');

    const values =
      source === 'product-card'
        ? sourceSelectedOptionsValues.length
          ? sourceSelectedOptionsValues
          : [
              selectedOption?.dataset.optionValueId,
            ].filter(Boolean)
        : this.selectedOptionsValues;

    if (values.length) {
      url.searchParams.set(
        'option_values',
        values.join(',')
      );
    }

    const current = new URL(window.location.href);

    for (const key of [
      'preview_theme_id',
      '_ab',
      '_fd',
      '_sc',
    ]) {
      if (current.searchParams.has(key)) {
        url.searchParams.set(
          key,
          current.searchParams.get(key)
        );
      }
    }

    const view = getViewParameterValue();

    if (view) {
      url.searchParams.set('view', view);
    }

    if (
      this.closest(
        'quick-add-component, ' +
          'swatches-variant-picker-component'
      )
    ) {
      url.searchParams.set(
        'section_id',
        'section-rendering-product-card'
      );
    } else {
      const featured = this.closest(
        'featured-product-information'
      );

      if (featured) {
        url.searchParams.set(
          'section_id',
          this.dataset.sectionId ||
            featured.id.replace(/^shopify-section-/, '')
        );
      }
    }

    return url.toString();
  }

  updateVariantUrl(
    variant = this.#json(
      this.querySelector('script[data-selected-variant]')
    ),
    productUrl
  ) {
    if (!this.#isProductPage()) return;

    const url = new URL(window.location.href);

    if (productUrl) {
      url.pathname = new URL(productUrl, url).pathname;
    }

    url.searchParams.delete('option_values');

    if (variant?.id) {
      url.searchParams.set(
        'variant',
        String(variant.id)
      );
    } else {
      url.searchParams.delete('variant');
    }

    if (url.href !== window.location.href) {
      history.replaceState(
        history.state,
        '',
        url.toString()
      );
    }
  }

    syncNutritionSelect(variantId) {
    if (!variantId) return;
    const productId = this.dataset.productId;
    if (!productId) return;
    const nutritionSelect = document.getElementById(`js-variant-nutrition-${productId}`);
    if (!nutritionSelect) return;
    const hasVariant = Array.from(nutritionSelect.options).some(
      (option) => String(option.value) === String(variantId)
    );
    if (!hasVariant) return;
    nutritionSelect.value = String(variantId);
    nutritionSelect.dispatchEvent(
      new Event('change', {
        bubbles: true
      })
    );
  }

  #findResponsePicker(html, changesProduct = false) {
    const pickers = Array.from(
      html.querySelectorAll('variant-picker')
    );

    return (
      pickers.find(
        (picker) =>
          picker.dataset.blockId ===
            this.dataset.blockId &&
          (
            changesProduct ||
            picker.dataset.productId ===
              this.dataset.productId
          ) &&
          picker.dataset.sectionId ===
            this.dataset.sectionId
      ) ||
      pickers.find(
        (picker) =>
          picker.dataset.blockId ===
            this.dataset.blockId &&
          (
            changesProduct ||
            picker.dataset.productId ===
              this.dataset.productId
          )
      ) ||
      (
        pickers.length === 1
          ? pickers[0]
          : undefined
      )
    );
  }

  #selectedFrom(groups) {
    return groups.map((group) => this.#selected(group));
  }

  /*
   * Correct server-rendered invalid combinations before
   * morphing the visible picker.
   */
  #correctResponse(picker, url) {
    const groups = this.#readGroups(picker);
    const primary = this.#primary(groups);

    for (const group of groups) {
      if (group === primary) continue;

      const selected = this.#selected(group);

      if (selected?.dataset.variantId) continue;

      const valid = this.#sort(
        group,
        group.values.filter(
          (value) => value.dataset.variantId
        )
      );

      if (!valid.length) continue;

      this.#setSelected(group, valid[0]);

      const next = new URL(url);

      next.searchParams.set(
        'option_values',
        this.#selectedFrom(groups)
          .map((value) => value?.dataset.optionValueId)
          .filter(Boolean)
          .join(',')
      );

      next.searchParams.delete('variant');

      if (next.href !== url) {
        return next.href;
      }
    }

    return null;
  }

  async fetchUpdatedSection(
    requestUrl,
    morphElementSelector,
    optionValueId = ''
  ) {
    this.#abortController?.abort();

    const controller = new AbortController();

    this.#abortController = controller;

    const sequence = ++this.#requestSequence;
    const deferred = ProductSelectEvent.createPromise();

    deferred.promise.catch(() => {});

    this.#showError('');
    this.setAttribute('aria-busy', 'true');

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId || '',
          title: this.dataset.productTitle || '',
          handle: this.dataset.productHandle || '',
        },
        selectedOptions: this.getAllSelectedOptions(),
        detail: {
          optionValueId,
        },
        promise: deferred.promise,
      })
    );

    try {
      let html;
      let picker;
      let variant;

      const visited = new Set();

      let url = new URL(
        requestUrl,
        window.location.href
      ).href;

      while (true) {
        if (
          visited.has(url) ||
          visited.size >= 4
        ) {
          throw new Error(
            'Could not resolve this variant combination.'
          );
        }

        visited.add(url);

        const response = await fetch(url, {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(
            `Variant request failed (${response.status}).`
          );
        }

        const text = await response.text();

        if (
          controller.signal.aborted ||
          sequence !== this.#requestSequence
        ) {
          throw new DOMException(
            'Superseded',
            'AbortError'
          );
        }

        html = new DOMParser().parseFromString(
          text,
          'text/html'
        );

        picker = this.#findResponsePicker(
          html,
          morphElementSelector === 'main'
        );

        if (!picker) {
          throw new Error(
            'Variant picker was not present in the response.'
          );
        }

        const script = picker.querySelector(
          'script[data-selected-variant], ' +
            'script[type="application/json"]:not([data-variant-map])'
        );

        if (!script) {
          throw new Error(
            'Selected variant data was not present in the response.'
          );
        }

        variant = this.#json(script);

        if (!variant) {
          const corrected = this.#correctResponse(
            picker,
            url
          );

          if (corrected) {
            url = corrected;
            continue;
          }
        }

        break;
      }

      if (
        controller.signal.aborted ||
        sequence !== this.#requestSequence
      ) {
        throw new DOMException(
          'Superseded',
          'AbortError'
        );
      }

      const responseId =
        picker.dataset.productId ||
        this.dataset.productId ||
        '';

      const responseUrl = picker.dataset.productUrl;

      const newProduct =
        responseId !== this.dataset.productId
          ? {
              id: responseId,
              url: responseUrl,
            }
          : undefined;

      const sourceId =
        optionValueId || this.selectedOptionId;

      const view = this.#json({
        textContent: picker
          .closest('[view-event-payload]')
          ?.getAttribute('view-event-payload'),
      });

      html
        .querySelectorAll('overflow-list[defer]')
        .forEach((node) =>
          node.removeAttribute('defer')
        );

      this.#committing = true;

      const shouldUpdateUrl = this.#isProductPage();

      if (morphElementSelector === 'main') {
        this.updateMain(html);
      } else if (morphElementSelector) {
        this.updateElement(
          html,
          morphElementSelector
        );
      } else {
        this.updateVariantPicker(html, picker);
      }

      if (shouldUpdateUrl) {
        this.updateVariantUrl(
          variant,
          morphElementSelector === 'main'
            ? responseUrl
            : undefined
        );
      }

      if (variant?.id) {
        this.syncNutritionSelect(variant.id);
      }

      this.#pendingRequestUrl = undefined;

      deferred.resolve({
        variant:
          view?.product?.selectedVariant ?? null,
        detail: {
          html,
          productId: responseId,
          newProduct,
          sourceId,
          resource: variant,
        },
      });
    } catch (error) {
      if (error.name === 'AbortError') {
        /*
        * User ઝડપથી બીજું variant પસંદ કરે ત્યારે જૂની
        * request intentionally cancel થાય છે.
        *
        * તેને actual error તરીકે reject કરવાથી themeના
        * listeners consoleમાં false AbortError બતાવે છે.
        */
        deferred.resolve({
          variant: null,

          detail: {
            html: null,

            productId:
              this.dataset.productId || '',

            sourceId:
              optionValueId ||
              this.selectedOptionId,

            resource: null,

            aborted: true,
          },
        });
      } else {
        deferred.reject(error);
      }

      /*
      * Selection માત્ર સાચી HTTP/JavaScript errorમાં
      * restore કરવી. Canceled requestમાં restore નહીં થાય.
      */
      if (
        error.name !== 'AbortError' &&
        sequence === this.#requestSequence
      ) {
        console.error(
          'VariantPicker:',
          error
        );

        for (const group of this.#groups) {
          const previous =
            group.values.find((value) =>
              this.#committedSelection.includes(
                value.dataset.optionValueId
              )
            );

          if (previous) {
            this.#setSelected(
              group,
              previous
            );
          }
        }

        this.#reconcile(true);

        this.#showError(
          'Unable to update this selection. Please select an option again.'
        );
      }
    } finally {
      this.#committing = false;

      if (sequence === this.#requestSequence) {
        this.removeAttribute('aria-busy');
        this.#pendingRequestUrl = undefined;
      }
    }
  }

  #showError(message) {
    const error = this.querySelector(
      '[data-variant-picker-error]'
    );

    if (error) {
      error.textContent = message;
      error.hidden = !message;
    }
  }

  updateVariantPicker(
    html,
    source = this.#findResponsePicker(html)
  ) {
    if (!source) {
      throw new Error(
        'No new variant picker source found.'
      );
    }

    const previousId = this.dataset.productId;

    const expanded =
      this.refs.overflowList instanceof OverflowList &&
      this.refs.overflowList.getAttribute('disabled') ===
        'true';

    morph(this, source, {
      ...MORPH_OPTIONS,

      getNodeKey: (node) =>
        node instanceof HTMLElement
          ? node.dataset.key
          : undefined,
    });

    this.#readPicker();
    this.#reconcile(true);
    this.updateVariantPickerCss();

    if (
      expanded &&
      this.refs.overflowList instanceof OverflowList
    ) {
      this.refs.overflowList.showAll();
    }

    return previousId !== this.dataset.productId
      ? {
          id: this.dataset.productId,
          url: this.dataset.productUrl,
        }
      : undefined;
  }

  updateElement(html, selector) {
    const element = this.closest(selector);

    const source = element?.id
      ? html.getElementById(element.id)
      : html.querySelector(selector);

    if (!element || !source) {
      throw new Error(
        `No new element source found for ${selector}`
      );
    }

    morph(element, source);
  }

  updateMain(html) {
    const main = document.querySelector('main');
    const source = html.querySelector('main');

    if (!main || !source) {
      throw new Error('No new main source found.');
    }

    morph(main, source);
  }

  updateFieldsetCss(index) {
    const fieldset = (
      this.refs.fieldsets || []
    )[index];

    if (!fieldset) return;

    for (const [state, selector] of [
      ['current', 'input:checked'],
      [
        'previous',
        'input[data-previous-checked="true"]',
      ],
    ]) {
      const width = fieldset
        .querySelector(selector)
        ?.closest('label')
        ?.offsetWidth;

      if (width) {
        fieldset.style.setProperty(
          `--pill-width-${state}`,
          `${width}px`
        );
      } else {
        fieldset.style.removeProperty(
          `--pill-width-${state}`
        );
      }
    }
  }

  updateVariantPickerCss() {
    (this.refs.fieldsets || []).forEach(
      (_, index) => this.updateFieldsetCss(index)
    );
  }

  get selectedOption() {
    return (
      this.#groups
        .flatMap((group) => [
          this.#selected(group),
        ])
        .find(
          (value) =>
            value?.dataset.optionValueId ===
            this.#lastChanged?.dataset.optionValueId
        ) ||
      this.#selectedFrom(this.#groups)
        .filter(Boolean)
        .at(-1)
    );
  }

  get selectedOptionId() {
    return this.selectedOption?.dataset.optionValueId;
  }

  get selectedOptionsValues() {
    return this.#selectedFrom(this.#groups)
      .map((value) => value?.dataset.optionValueId)
      .filter(Boolean);
  }

  getAllSelectedOptions() {
    return this.#groups.map((group) => ({
      name: group.name,
      value: this.#selected(group)?.value || '',
    }));
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define(
    'variant-picker',
    VariantPicker
  );
}